import { phoneNumberValidator } from '@shared/libs/phone-validator';
import { TrashIcon, SixDotsIcon } from '@shared/assets/icons';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import { InputHTMLAttributes, useId, useRef } from 'react';
import { FIELD_ID_PHONE } from '@entities/leadCollector';
import { palette } from '@shared/tokens/palette.stylex';
import { colors } from '@shared/tokens/colors.stylex';
import stylex from '@stylexjs/stylex';
import { styles } from './styles';

const CUSTOM_FIELD_PLACEHOLDER = 'Название поля';

interface FormInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  id?: string;
  onDelete?: () => void;
  isLabelEditable?: boolean;
  onLabelChange?: (value: string) => void;
  isCanEdit: boolean;
  onInputChange?: (value: string) => void;
  error?: string;
}

export const FormInput = ({
  label,
  id,
  onDelete,
  isLabelEditable,
  onLabelChange,
  isCanEdit,
  onInputChange,
  error,
  ...props
}: FormInputProps) => {
  const autoId = useId();
  const uniqId = id ?? autoId;
  const labelRef = useRef<HTMLSpanElement>(null);
  const isPhoneField = uniqId === FIELD_ID_PHONE;

  const handleDeleteField = () => onDelete?.();

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isPhoneField) {
      handlePhoneChange(e);
      return;
    }

    onInputChange?.(e.target.value);
  };

  const handleContentEditableBlur = (e: React.FocusEvent<HTMLSpanElement>) => {
    if (labelRef.current && !e.currentTarget.textContent?.trim()) {
      labelRef.current.textContent = CUSTOM_FIELD_PLACEHOLDER;
    }
    onLabelChange?.(e.currentTarget.textContent?.trim() || '');
  };

  const handleContentEditableFocus = () => {
    if (labelRef.current?.textContent === CUSTOM_FIELD_PLACEHOLDER) {
      labelRef.current.textContent = '';
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = phoneNumberValidator(e.target.value);
    onInputChange?.(e.target.value);
  };

  return (
    <div {...stylex.props(styles.container(label))}>
      <div {...stylex.props(styles.content)}>
        {(label || isLabelEditable) &&
          (isLabelEditable ? (
            <span
              {...stylex.props(styles.editableLabelContent)}
              suppressContentEditableWarning
              contentEditable
              role="textbox"
              onBlur={handleContentEditableBlur}
              onFocusCapture={handleContentEditableFocus}
              ref={labelRef}
              onMouseDownCapture={() => labelRef.current?.focus()}
            >
              {labelRef.current?.textContent || label || CUSTOM_FIELD_PLACEHOLDER}
            </span>
          ) : (
            <label {...stylex.props(styles.label)} htmlFor={uniqId}>
              {label}
            </label>
          ))}

        <div {...stylex.props(styles.inputWrapper)}>
          <input
            {...stylex.props(styles.input, !!error && styles.inputError)}
            {...props}
            id={uniqId}
            onChange={handleChangeInput}
            autoComplete="off"
          />

          {!isCanEdit && (
            <>
              <UnstyledButton onClick={handleDeleteField} onMouseDown={(e) => e.stopPropagation()}>
                <span {...stylex.props(styles.gripIcon)}>
                  <SixDotsIcon color={colors.secondaryBackground} />
                </span>
              </UnstyledButton>

              <UnstyledButton onClick={handleDeleteField}>
                <span {...stylex.props(styles.removeButton)}>
                  <TrashIcon color={palette['grey-950']} />
                </span>
              </UnstyledButton>
            </>
          )}
        </div>

        {error && <span {...stylex.props(styles.textError)}>{error}</span>}
      </div>
    </div>
  );
};
